import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Heart, MessageSquare, MapPin, Quote, Sparkles, ArrowRight } from "lucide-react";

export default function TruthMarkerPage() {
  const { tm_id } = useParams();
  const [data, setData] = useState(null);
  const [needed, setNeeded] = useState(false);
  const [reflection, setReflection] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = () => {
    http.get(`/m/${tm_id}`).then(({ data }) => setData(data)).catch(() => setData({ marker: null }));
  };

  useEffect(() => {
    reload();
    setNeeded(localStorage.getItem(`tm_needed_${tm_id}`) === "1");
  }, [tm_id]);

  if (!data) return <Layout><div className="min-h-[60vh] flex items-center justify-center text-[#8a857d]">Loading…</div></Layout>;
  if (!data.marker) return <Layout><div className="min-h-[60vh] flex items-center justify-center text-[#8a857d]">Marker not found.</div></Layout>;

  const m = data.marker;

  const tapNeeded = async () => {
    if (needed) return;
    try {
      const { data: r } = await http.post(`/m/${tm_id}/needed`);
      localStorage.setItem(`tm_needed_${tm_id}`, "1");
      setNeeded(true);
      setData({ ...data, marker: { ...m, needed_count: r.needed_count } });
    } catch { toast.error("Couldn't record."); }
  };

  const submitReflection = async (e) => {
    e?.preventDefault?.();
    if (reflection.trim().length < 3) { toast.error("Share a few words."); return; }
    setBusy(true);
    try {
      await http.post(`/m/${tm_id}/reflection`, { text: reflection.trim(), name: name.trim() || null });
      setReflection(""); setName("");
      toast.success("Thank you for sharing.");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't submit."); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      {/* HERO */}
      <section className="relative bg-[#050505] pt-20 pb-16 sm:pt-28 sm:pb-20" data-testid="tm-page-hero">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-5 inline-flex items-center gap-2">
            <MapPin size={11} /> Part of the Truth Marker Project · {m.tm_id}
          </div>
          {m.photo_url && (
            <div className="mb-7 max-w-md mx-auto aspect-[4/3] overflow-hidden border border-white/10">
              <img src={m.photo_url} alt={m.location_name} className="w-full h-full object-cover" />
            </div>
          )}
          <Quote size={28} className="mx-auto text-[#C9A055]/60 mb-4" />
          <blockquote className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F4F1EA] leading-[1.2]">
            &ldquo;{m.truth}&rdquo;
          </blockquote>
          <div className="mt-5 font-ui text-[11px] tracking-[0.22em] uppercase text-[#8a857d]">
            <MapPin size={11} className="inline -mt-0.5 mr-1" />
            {m.location_name}{m.address ? ` · ${m.address}` : ""}
          </div>
        </div>
      </section>

      {/* STORY */}
      {m.story && (
        <section className="bg-[#0a0a0a] py-12 sm:py-16 border-y border-white/5" data-testid="tm-page-story">
          <div className="max-w-2xl mx-auto px-6">
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4 text-center">The Story</div>
            <div className="w-12 h-px bg-[#C9A055] mx-auto mb-8" />
            <p className="font-body text-[16px] leading-[1.85] text-[#B5B0A7] whitespace-pre-line text-center">{m.story}</p>
          </div>
        </section>
      )}

      {/* I NEEDED THIS */}
      <section className="bg-[#050505] py-12 border-y border-white/5" data-testid="tm-page-needed">
        <div className="max-w-md mx-auto px-6 text-center">
          <button onClick={tapNeeded} disabled={needed} data-testid="tm-page-needed-btn"
                  className={`inline-flex items-center gap-3 px-6 py-4 border transition ${needed ? "border-[#C9A055] bg-[#C9A055]/10 text-[#C9A055]" : "border-white/15 text-[#F4F1EA] hover:border-[#C9A055]"}`}>
            <Heart size={18} fill={needed ? "#C9A055" : "none"} />
            <span className="font-display text-lg">{needed ? "Glad you found it." : "I needed this today."}</span>
          </button>
          <div className="mt-4 font-ui text-[11px] tracking-[0.22em] uppercase text-[#8a857d]">
            {(m.needed_count || 0).toLocaleString()} people said they needed this
          </div>
        </div>
      </section>

      {/* REFLECTION */}
      <section className="bg-[#0a0a0a] py-14 border-y border-white/5" data-testid="tm-page-reflection">
        <div className="max-w-xl mx-auto px-6">
          <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-3 text-center">Reflect</div>
          <h2 className="font-display text-2xl text-[#F4F1EA] text-center mb-2">What does this truth say to you?</h2>
          <div className="w-12 h-px bg-[#C9A055] mx-auto mb-7" />
          <form onSubmit={submitReflection} className="space-y-3">
            <textarea data-testid="tm-page-reflection-text" className="stc-input min-h-[100px]" maxLength={600}
                      placeholder="Share what this truth means to you…" value={reflection}
                      onChange={(e) => setReflection(e.target.value)} />
            <div className="flex gap-3">
              <input data-testid="tm-page-reflection-name" className="stc-input flex-1" maxLength={80}
                     placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
              <button type="submit" disabled={busy} data-testid="tm-page-reflection-submit"
                      className="btn-gold flex items-center gap-2 disabled:opacity-60">
                <ArrowRight size={14} /> Share
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* WHAT OTHERS SAY */}
      {data.reflections?.length > 0 && (
        <section className="bg-[#050505] py-14 border-y border-white/5" data-testid="tm-page-others">
          <div className="max-w-2xl mx-auto px-6">
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-3 text-center">What Others Are Saying</div>
            <div className="w-12 h-px bg-[#C9A055] mx-auto mb-8" />
            <div className="space-y-4">
              {data.reflections.map((rf, i) => (
                <div key={i} className="border border-white/10 bg-[#0a0a0a] p-4">
                  <MessageSquare size={12} className="text-[#C9A055] opacity-70 mb-2" />
                  <p className="font-body text-[14px] text-[#F4F1EA] leading-relaxed italic">&ldquo;{rf.text}&rdquo;</p>
                  <div className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] mt-2">— {rf.name}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TRUTH REGISTRY STATS */}
      <section className="bg-[#0a0a0a] py-14 border-y border-white/5" data-testid="tm-page-stats">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">Truth Registry</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-7">
            <Stat label="Marker" value={m.tm_id} />
            <Stat label="Scans" value={(m.scans || 0).toLocaleString()} />
            <Stat label="Reflections" value={(data.reflections?.length || 0).toLocaleString()} />
            <Stat label="Needed It" value={(m.needed_count || 0).toLocaleString()} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#050505] py-14">
        <div className="max-w-md mx-auto px-6 text-center">
          <Sparkles size={20} className="mx-auto text-[#C9A055] mb-4" />
          <div className="font-display text-xl text-[#F4F1EA] mb-3">Got a truth of your own?</div>
          <Link to="/truth-wall" className="btn-gold inline-flex items-center gap-2">Submit a Truth <ArrowRight size={14} /></Link>
        </div>
      </section>
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/10 bg-[#050505] p-4">
      <div className="font-display text-[#F4F1EA] text-base leading-tight">{value || "—"}</div>
      <div className="font-ui text-[9px] tracking-[0.22em] uppercase text-[#8a857d] mt-1">{label}</div>
    </div>
  );
}
